import LeaseRecordInput from 'src/components/leases/LeaseRecordInput';
import React, { useEffect, useState } from 'react';
import { BiChevronDown } from 'react-icons/bi';
import { ILeaseMaster } from 'src/models/dlcLease/ILeaseMaster';
import * as styles from './styles';

interface IClientAddressSectionProps {
    leaseMaster: ILeaseMaster;
    onChange?: (field: string, value: unknown) => void;
}

const ClientAddressSection: React.FC<IClientAddressSectionProps> = ({ leaseMaster, onChange }) => {
    const clientAddress = leaseMaster.clientAddress;
    const isEditable = !!onChange;
    const [isExpanded, setIsExpanded] = useState(false);

    useEffect(() => {
        setIsExpanded(false);
    }, [leaseMaster.lsNo]);

    const handleChange = (field: string, value: string) => {
        if (onChange) {
            onChange(`clientAddress.${field}`, value);
        }
    };

    return (
        <div style={styles.sectionContainer}>
            <h2 style={{ ...styles.sectionHeader, marginBottom: isExpanded ? '0.75rem' : 0 }}>
                <span style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={styles.colors.primary} strokeWidth="2">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                        <circle cx="12" cy="10" r="3" />
                    </svg>
                    Client Address
                </span>
                <button
                    type="button"
                    aria-label={isExpanded ? 'Collapse Client Address' : 'Expand Client Address'}
                    aria-expanded={isExpanded}
                    aria-controls="client-address-content"
                    title={isExpanded ? 'Collapse Client Address' : 'Expand Client Address'}
                    onClick={() => setIsExpanded((current) => !current)}
                    style={{
                        width: '2rem',
                        height: '2rem',
                        padding: 0,
                        border: `1px solid ${styles.colors.gray300}`,
                        borderRadius: '6px',
                        backgroundColor: styles.colors.bgPrimary,
                        color: styles.colors.gray700,
                        cursor: 'pointer',
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                    }}
                >
                    <BiChevronDown
                        aria-hidden="true"
                        size={22}
                        style={{
                            transition: 'transform 150ms ease',
                            transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                        }}
                    />
                </button>
            </h2>

            {isExpanded &&
                (!clientAddress && !isEditable ? (
                    <div id="client-address-content" style={styles.emptyState}>
                        <svg
                            width="32"
                            height="32"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke={styles.colors.gray400}
                            strokeWidth="1.5"
                            style={{ margin: '0 auto 0.5rem' }}
                        >
                            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" />
                            <circle cx="12" cy="10" r="3" />
                            <line x1="12" y1="21" x2="12" y2="23" />
                            <line x1="7" y1="21" x2="17" y2="21" />
                        </svg>
                        <p style={{ margin: 0, color: styles.colors.gray600, fontSize: styles.typography.fontSize.sm }}>
                            No client address information available
                        </p>
                        {isEditable && (
                            <p style={{ marginTop: '0.5rem', fontSize: styles.typography.fontSize.sm, color: styles.colors.gray500 }}>
                                Add client address information to complete this section
                            </p>
                        )}
                    </div>
                ) : (
                    <div id="client-address-content" style={{ display: 'grid', gap: '0.75rem' }}>
                        <div
                            style={{
                                padding: '0.75rem',
                                backgroundColor: styles.colors.gray50,
                                borderRadius: '6px',
                                border: `1px solid ${styles.colors.gray200}`,
                            }}
                        >
                            <h3
                                style={{
                                    margin: '0 0 0.5rem 0',
                                    fontSize: styles.typography.fontSize.sm,
                                    fontWeight: styles.typography.fontWeight.semibold,
                                    color: styles.colors.gray800,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                }}
                            >
                                Client Information
                            </h3>
                            <div style={styles.gridCols2}>
                                <Field
                                    label="Primary Name"
                                    value={clientAddress?.name1 || ''}
                                    editable={isEditable}
                                    onChange={(value) => handleChange('name1', value)}
                                    required={true}
                                />
                                {(clientAddress?.name2 || isEditable) && (
                                    <Field
                                        label="Secondary Name"
                                        value={clientAddress?.name2 || ''}
                                        editable={isEditable}
                                        onChange={(value) => handleChange('name2', value)}
                                    />
                                )}
                                {(clientAddress?.name3 || isEditable) && (
                                    <Field
                                        label="Additional Name"
                                        value={clientAddress?.name3 || ''}
                                        editable={isEditable}
                                        onChange={(value) => handleChange('name3', value)}
                                    />
                                )}
                                {(clientAddress?.name4 || isEditable) && (
                                    <Field
                                        label="Other Name"
                                        value={clientAddress?.name4 || ''}
                                        editable={isEditable}
                                        onChange={(value) => handleChange('name4', value)}
                                    />
                                )}
                            </div>
                        </div>

                        {/* Address Section */}
                        <div
                            style={{
                                padding: '0.75rem',
                                backgroundColor: styles.colors.gray50,
                                borderRadius: '6px',
                                border: `1px solid ${styles.colors.gray200}`,
                            }}
                        >
                            <h3
                                style={{
                                    margin: '0 0 0.5rem 0',
                                    fontSize: styles.typography.fontSize.sm,
                                    fontWeight: styles.typography.fontWeight.semibold,
                                    color: styles.colors.gray800,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                }}
                            >
                                Location
                            </h3>
                            <div style={styles.gridCols3}>
                                <Field
                                    label="City"
                                    value={clientAddress?.city || ''}
                                    editable={isEditable}
                                    onChange={(value) => handleChange('city', value)}
                                />
                                <Field
                                    label="State"
                                    value={clientAddress?.state || ''}
                                    editable={isEditable}
                                    onChange={(value) => handleChange('state', value)}
                                    placeholder="TX"
                                />
                                <Field
                                    label="ZIP Code"
                                    value={clientAddress?.zip || ''}
                                    editable={isEditable}
                                    onChange={(value) => handleChange('zip', value)}
                                    placeholder="12345"
                                />
                            </div>
                        </div>

                        {/* Contact Section */}
                        <div
                            style={{
                                padding: '0.75rem',
                                backgroundColor: styles.colors.gray50,
                                borderRadius: '6px',
                                border: `1px solid ${styles.colors.gray200}`,
                            }}
                        >
                            <h3
                                style={{
                                    margin: '0 0 0.5rem 0',
                                    fontSize: styles.typography.fontSize.sm,
                                    fontWeight: styles.typography.fontWeight.semibold,
                                    color: styles.colors.gray800,
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                }}
                            >
                                Contact Details
                            </h3>
                            <div style={styles.gridCols3}>
                                <Field
                                    label="Phone"
                                    value={clientAddress?.phone || ''}
                                    editable={isEditable}
                                    onChange={(value) => handleChange('phone', value)}
                                    type="tel"
                                    placeholder="(555) 123-4567"
                                />
                                <Field
                                    label="Fax"
                                    value={clientAddress?.fax || ''}
                                    editable={isEditable}
                                    onChange={(value) => handleChange('fax', value)}
                                    type="tel"
                                    placeholder="(555) 123-4568"
                                />
                                <Field
                                    label="Email"
                                    value={clientAddress?.email || ''}
                                    editable={isEditable}
                                    onChange={(value) => handleChange('email', value)}
                                    type="email"
                                    placeholder="client@example.com"
                                />
                            </div>
                        </div>
                    </div>
                ))}
        </div>
    );
};

const Field = ({
    label,
    value,
    editable = false,
    onChange,
    type = 'text',
    placeholder,
    required = false,
}: {
    label: string;
    value: string;
    editable?: boolean;
    onChange?: (value: string) => void;
    type?: string;
    placeholder?: string;
    required?: boolean;
}) => (
    <div style={styles.fieldContainer}>
        <label
            style={{
                ...styles.fieldLabel,
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
            }}
        >
            {label}
            {required && <span style={{ color: styles.colors.danger }}>*</span>}
        </label>
        <LeaseRecordInput
            type={type}
            value={value}
            disabled={!editable}
            onChange={(e) => onChange?.(e.target.value)}
            placeholder={editable ? placeholder : ''}
            autoComplete="off"
            onFocus={(e) => {
                if (editable) {
                    e.currentTarget.style.borderColor = styles.colors.borderFocus;
                    e.currentTarget.style.boxShadow = styles.inputFocus.boxShadow;
                }
            }}
            onBlur={(e) => {
                e.currentTarget.style.borderColor = styles.colors.gray300;
                e.currentTarget.style.boxShadow = 'none';
            }}
            style={editable ? styles.inputBase : styles.inputDisabled}
        />
    </div>
);

export default ClientAddressSection;



// WEBPACK FOOTER //
// ./src/components/leases/sections/ClientAddress.tsx